import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import Layout from '../../components/Layout';
import { purchaseOrderService, PurchaseOrderDTO, ApprovalStatus } from '../../services/purchaseOrderService';
import './PurchaseOrderPage.css';

const APPROVAL_BADGE: Record<ApprovalStatus, string> = {
  Draft:           'ims-badge ims-badge-draft',
  PendingApproval: 'ims-badge ims-badge-pending',
  Approved:        'ims-badge ims-badge-approved',
  Rejected:        'ims-badge ims-badge-rejected',
};

const PurchaseOrderApprovalsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t }    = useTranslation();

  const [items,   setItems]   = useState<PurchaseOrderDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await purchaseOrderService.getPurchaseOrders({ pageNumber: 1, pageSize: 500 });
      const body = res?.data as any;
      const all: PurchaseOrderDTO[] = body?.data?.data ?? body?.data ?? body ?? [];
      // Show everything that is in the approval workflow (not draft, not closed/cancelled)
      setItems(all.filter(p =>
        p.status !== 'Draft' &&
        p.status !== 'Closed' &&
        p.approvalStatus !== 'Approved'
      ));
    } catch {
      setError(t('purchaseOrder.approval.failedToLoadPending'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const langCode = t('common.languageCode') === 'ja' ? 'ja-JP' : 'en-US';
  const fmt = (v: number) =>
    new Intl.NumberFormat(langCode, { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);

  const pendingApprovalCount = items.filter(p => p.approvalStatus === 'PendingApproval').length;
  const approvedCount        = items.filter(p => p.approvalStatus === 'Approved').length;
  const rejectedCount        = items.filter(p => p.approvalStatus === 'Rejected').length;

  return (
    <Layout>
      <div className="po-approvals-page">

        {/* Header */}
        <div className="ims-list-header">
          <div>
            <h1 className="ims-list-title">{t('purchaseOrder.approval.pendingApprovalsTitle')}</h1>
            <p style={{ color: 'var(--ims-text-sub)', fontSize: 13, marginTop: 2 }}>
              {t('purchaseOrder.approval.pendingApprovalsSubtitle')}
            </p>
          </div>
          <div className="ims-list-actions">
            <button className="btn-refresh" onClick={fetchAll} disabled={loading}>
              <RefreshCw size={14} strokeWidth={2} className={loading ? 'refresh-icon-spinning' : ''} />
              {t('common.refresh')}
            </button>
            <button className="ims-btn ims-btn-secondary" onClick={() => navigate('/purchase/purchase-orders')}>
              {t('purchaseOrder.backToPurchaseOrders')}
            </button>
          </div>
        </div>

        {/* Stat chips */}
        {!loading && (
          <div className="ims-stats-row" style={{ marginBottom: 16 }}>
            <div className="ims-stat-card">
              <div className="ims-stat-info">
                <span className="ims-stat-value">{items.length}</span>
                <span className="ims-stat-label">{t('purchaseOrder.approval.statTotal')}</span>
              </div>
            </div>
            <div className="ims-stat-card">
              <div className="ims-stat-info">
                <span className="ims-stat-value">{pendingApprovalCount}</span>
                <span className="ims-stat-label">{t('purchaseOrder.approval.statPendingApproval')}</span>
              </div>
            </div>
            <div className="ims-stat-card">
              <div className="ims-stat-info">
                <span className="ims-stat-value">{approvedCount}</span>
                <span className="ims-stat-label">{t('purchaseOrder.approval.statApproved')}</span>
              </div>
            </div>
            <div className="ims-stat-card">
              <div className="ims-stat-info">
                <span className="ims-stat-value">{rejectedCount}</span>
                <span className="ims-stat-label">{t('purchaseOrder.approval.statRejected')}</span>
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="ims-alert ims-alert-error" style={{ margin: '0 0 16px' }}>
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="ims-loading" style={{ padding: '48px 0' }}>
            <div className="spinner-border" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!loading && items.length === 0 && (
          <div style={{
            textAlign: 'center', padding: '64px 24px',
            color: 'var(--ims-text-muted)', background: '#fff',
            borderRadius: 12, border: '1px solid var(--ims-border)',
          }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--ims-text-main)', marginBottom: 6 }}>
              {t('purchaseOrder.approval.noApprovalsPending')}
            </div>
            <div style={{ fontSize: 13 }}>{t('purchaseOrder.approval.noApprovalsPendingSubtext')}</div>
          </div>
        )}

        {/* PO cards */}
        {!loading && items.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {items.map(item => (
              <div key={item.id} className="ims-card" style={{ margin: 0 }}>
                <div style={{
                  display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                  flexWrap: 'wrap', gap: 12, padding: '16px 20px',
                }}>
                  {/* Left: PO info */}
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>
                        {item.poNumber}
                      </span>
                      <span className={APPROVAL_BADGE[item.approvalStatus] ?? 'ims-badge ims-badge-pending'}>
                        {t(`purchaseOrder.approval.status${item.approvalStatus}`)}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--ims-text-sub)', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                      {item.supplierName && (
                        <span><strong>{t('purchaseOrder.supplier')}:</strong> {item.supplierName}</span>
                      )}
                      <span><strong>{t('purchaseOrder.orderDate')}:</strong> {new Date(item.orderDate).toLocaleDateString(langCode)}</span>
                      <span><strong>{t('purchaseOrder.totalAmount')}:</strong> {fmt(item.totalAmount)}</span>
                      <span>
                        <strong>{t('purchaseOrder.approval.level')}:</strong>{' '}
                        {item.currentApprovalLevel} / {item.totalApprovalLevels}
                      </span>
                    </div>
                    {item.approvalStatus === 'PendingApproval' && (
                      <div style={{ marginTop: 6, fontSize: 12, color: '#0e7490', fontWeight: 500 }}>
                        {t('purchaseOrder.approval.awaitingApproval')}
                      </div>
                    )}
                    {item.approvalStatus === 'Approved' && (
                      <div style={{ marginTop: 6, fontSize: 12, color: '#15803d', fontWeight: 500 }}>
                        {t('purchaseOrder.approval.readyToProcess')}
                      </div>
                    )}
                    {item.approvalStatus === 'Rejected' && (
                      <div style={{ marginTop: 6, fontSize: 12, color: '#991b1b', fontWeight: 500 }}>
                        {t('purchaseOrder.approval.statusRejected')}
                      </div>
                    )}
                  </div>

                  {/* Right: action */}
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                    <button
                      className="ims-btn ims-btn-secondary"
                      style={{ fontSize: 13 }}
                      onClick={() => navigate(`/purchase-orders/${item.id}`)}
                    >
                      {item.approvalStatus === 'Approved'
                        ? t('purchaseOrder.approval.reviewAndAct')
                        : t('common.view')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    </Layout>
  );
};

export default PurchaseOrderApprovalsPage;
